import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiPalette, mdiPlus } from '@mdi/js'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { AppIcon } from '../ui/icon'
import { EmptyState } from '../ui/empty-state'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { useCan } from '../../contexts/PermissionsContext'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { pickLabel } from './colour'
import { VIEWS, classCount } from './definition'
import { useThematicDefinitions } from './definitionApi'

/**
 * The stored thematics of the organization (spec 2026-10-06-11): what each one
 * colours, where it reads its value, how many classes it has and the views it is
 * suggested in. Editing needs `configuration.write`; the list is read-only
 * without it.
 */
export function ThematicDefinitionsPage({ org }: { org: string }) {
  const { t, i18n } = useTranslation('thematics')
  const lang = i18n.language || 'en'
  const canWrite = useCan('configuration.write')
  const { data, isLoading, isError } = useThematicDefinitions(org)

  useBreadcrumbs([
    { title: t('definitions.breadcrumb.home'), path: `/${org}` },
    { title: t('definitions.breadcrumb.types'), path: `/${org}/types` },
    { title: t('definitions.breadcrumb.thematics'), path: `/${org}/types/thematics` },
  ])

  return (
    <div className="space-y-6" data-testid="thematic-definitions-page">
      <PageHeader
        icon={SECTION_ICONS.types}
        title={t('definitions.title')}
        description={t('definitions.description')}
        actions={canWrite && (
          <Button asChild data-testid="thematic-definition-new">
            <Link to="/$org/types/thematics/$code" params={{ org, code: 'new' }}>
              <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
              <span className="hidden md:inline">{t('definitions.new')}</span>
            </Link>
          </Button>
        )}
      />
      {!canWrite && <p className="text-sm text-muted-foreground" data-testid="thematic-definitions-read-only">{t('definitions.read_only')}</p>}
      {isError && <p className="text-sm text-destructive">{t('definitions.load_error')}</p>}
      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <EmptyState icon={mdiPalette} title={t('definitions.empty')} description={t('definitions.empty_description')} />
      )}
      {(data?.length ?? 0) > 0 && (
        <Table data-testid="thematic-definitions-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('definitions.col.name')}</TableHead>
              <TableHead>{t('definitions.col.target')}</TableHead>
              <TableHead>{t('definitions.col.source')}</TableHead>
              <TableHead className="text-right">{t('definitions.col.classes')}</TableHead>
              <TableHead>{t('definitions.col.suggested')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.map((d) => (
              <TableRow key={d.code} data-testid={`thematic-definition-row-${d.code}`}>
                <TableCell>
                  <Link
                    to="/$org/types/thematics/$code"
                    params={{ org, code: d.code }}
                    className="font-medium underline-offset-2 hover:underline"
                    data-testid={`thematic-definition-link-${d.code}`}
                  >
                    {pickLabel(d.name, lang, d.code)}
                  </Link>
                  <div className="font-mono text-xs text-muted-foreground">{d.code}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {d.targets.map((x) => <Badge key={x} variant="secondary">{t(`definitions.target.${x}`)}</Badge>)}
                  </div>
                </TableCell>
                <TableCell className="text-sm" data-testid={`thematic-definition-source-${d.code}`}>
                  {d.source.kind === 'property' ? d.source.property_code : t(`definitions.source_kind.${d.source.kind}`)}
                </TableCell>
                <TableCell className="text-right" data-testid={`thematic-definition-classes-${d.code}`}>
                  {(d.classes?.length ?? 0) === 0 ? t('definitions.one_per_value') : classCount(d)}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {VIEWS.filter((v) => d.suggested_in?.[v] !== undefined).map((v) => (
                      <Badge key={v} variant="outline">{t(`definitions.view.${v}`)} #{d.suggested_in?.[v]}</Badge>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
